import RegexAPI from "restAPI/RegexAPI";
import { some, isEmpty } from "./lodashUtils";
import { isArray } from "lodash";
import { ip6Re, ipRe } from "constants/RegExp";

export function duplicateNameValidator({
  message = "Name already exists",
  list,
}) {
  return {
    validator: (rule, value) => {
      if(value) {
        const isDuplicateName = some(
          list,
          (item) => item.name.toLowerCase() === value.toLowerCase()
        );
        if (isDuplicateName) {
          return Promise.reject(message);
        }
      }
      return Promise.resolve();
    },
  };
}

export const regexValidator = (message = "Invalid Regex") => {
  return {
    validator: (rule, value) => {
      try {
        new RegExp(value);
        return Promise.resolve();
      } catch(e) {
        return Promise.reject(message);
      }
    },
  };
}

export const validateRegexFields = async (form, regexFields) => {
  const data = form.getFieldsValue();
  const promises = [];
  regexFields.forEach(field => {
      const regexValue = data[field];
      if (regexValue && regexValue.length > 0) {
          promises.push(RegexAPI.validateRegex(regexValue).promise);
      }
  });

  const responses = await Promise.allSettled(promises);
  let hasErrors = false;
  responses.forEach((resp, index) => {
      const result = resp?.value || resp?.reason?.response?.data;
      if (!result.data) {
          hasErrors = true;
          form.setFields([{
              name: regexFields[index],
              errors: [result?.message || 'Invalid regex'],
          }]);
      }
  });
  return hasErrors;
};

export function noLocalhostValidator() {
    return {
        validator: (_rule, value) => {
            if (isEmpty(value)) {
                return Promise.resolve();
            }

            let hostname = '';
            try {
                hostname = new URL(value).hostname;
            } catch (e) {
                // fallback: try to extract hostname manually
                hostname = value;
            }
            hostname = hostname.toLowerCase();

            // Block localhost and variants
            const blockedHosts = [
                'localhost',
                'loopback',
                'localhost.localdomain',
                'host.docker.internal'
            ];
            if (
                blockedHosts.includes(hostname) ||
                hostname.endsWith('.localhost')
            ) {
                return Promise.reject(new Error('Localhost or internal hosts are not allowed.'));
            }

            return Promise.resolve();
        }
    }
}

export const validateBlankSpace = (errorMessage = "Field can't contain blank spaces.") => {
  return {
    validator: (_, values) => {
      const regEx = /\s/;
      if (values) {
        values = isArray(values) ? values : [values];
        for (let i = 0; i < values.length; i++) {
          if (regEx.test(values[i])) {
            return Promise.reject(errorMessage);
          }
        }
      }
      return Promise.resolve();
    },
  };
};

/**
 * Synchronous IP address validator (single IPs only).
 * Accepts: full IPv4 and full IPv6. Rejects ranges.
 * Returns error message string if invalid, or undefined if valid.
 */
export function validateIPAddress(value) {
    if (!value) return undefined;

    // Detect IP range format (exactly two parts that look like IPs separated by hyphen)
    if (value.includes("-")) {
        const parts = value.split("-");
        if (parts.length === 2 && ipRe.test(parts[0].trim()) && ipRe.test(parts[1].trim())) {
            return "Enter a single IP address, not a range.";
        }
    }

    if (ipRe.test(value) || ip6Re.test(value)) {
        return undefined;
    }

    return "Not a valid IP address.";
}

/**
 * Synchronous IP range validator (ranges only).
 * Accepts: IPv4 ranges (e.g. 10.0.0.0-10.255.255.255). Rejects single IPs.
 * Returns error message string if invalid, or undefined if valid.
 */
export function validateIPRange(value) {
    if (!value) return undefined;

    if (!value.includes("-")) {
        return "Enter an IP range (e.g. 192.168.1.1-192.168.1.100).";
    }

    const parts = value.split("-");
    if (parts.length === 2) {
        const [start, end] = parts.map((p) => p.trim());
        if (start && end && ipRe.test(start) && ipRe.test(end)) {
            return undefined;
        }
    }

    return "Not a valid IP range.";
}

export function ipV4OnlyValidator() {
    return {
        validator: (_rule, value) => {
            if (!isEmpty(value) && value.endsWith('/')) {
                return Promise.reject(new Error('IP should not end with /.'));
            }

            if (!isEmpty(value) && !ipRe.test(value)) {
                return Promise.reject(new Error('Not a valid IP.'))
            } else {
                return Promise.resolve();
            }
        }
    }
}

export function ipV6OnlyValidator() {
    return {
        validator: (_rule, value) => {
            if (!isEmpty(value) && value.endsWith('/')) {
                return Promise.reject(new Error('IP should not end with /.'));
            }

            if (!isEmpty(value) && !ip6Re.test(value)) {
                return Promise.reject(new Error('Not a valid IP.'))
            } else {
                return Promise.resolve();
            }
        }
    }
}

export function urlRegexValidatorWithVariables() {
    return {
        validator: (_rule, value) => {
            // Allow URL and IP, with or without (protocol/port/query params).
            // Also allows template variables (${...}) anywhere including the hostname.
            // eslint-disable-next-line
            const urlRe = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?(\$\{[^}]+\}|[a-z0-9])([\-\.\_\/]?(\$\{[^}]+\}|[a-z0-9]))*(:[0-9]{1,5})?(\/[a-zA-Z0-9-%_.#: ${}]*)*(\?[a-zA-Z0-9=%&._\[\]+~,:-{}()$]*)?(\#[a-zA-Z0-9_${}-]+)?$/i;

            if (!isEmpty(value) && value.endsWith('/')) {
                return Promise.reject(new Error('URL should not end with /.'));
            }

            if (!isEmpty(value) && !urlRe.test(value) && !ipRe.test(value) && !ip6Re.test(value)) {
                return Promise.reject(new Error('Not a valid URL/IP.'))
            } else {
                return Promise.resolve();
            }
        }
    }
}
